import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Badge, Box, DropdownMenu, DropdownMenuItem, IconButton, Page, Tabs, Typography } from 'seagull';
import { Edit, Kebab } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { useStateFromUrl } from 'seagull/utils/useStateFromUrl';
import { useOpenEditClientModal } from '~/components/EditClientModal/hooks/useOpenEditClientModal';
import { routes } from '~/constants';
import { useGlobalBillClient } from '~/hooks/useGlobalBillClient';
import { useLocaleKeys } from '~/i18n';
import styles from './ClientDetailPage.module.scss';
import { CLIENT_DETAIL_TABS, DEFAULT_CLIENT_DETAIL_TAB } from './ClientDetailPage.types';
import { IssuedInvoicesTab } from './tabs/IssuedInvoicesTab/IssuedInvoicesTab';
import { SummaryTab } from './tabs/SummaryTab/SummaryTab';
import { UpcomingInvoicesTab } from './tabs/UpcomingInvoicesTab/UpcomingInvoicesTab';
export const clientDetailPageTextsPath = 'pages.clientDetail';
export const GetClientDetailPageTID = (parentTestId)=>getTestIds(parentTestId);
export function ClientDetailPage(param) {
    let { clients = [], match } = param;
    const clientId = match.params.clientId;
    const history = useHistory();
    const { ClientDetailPageTestIds: TID } = useMemo(()=>GetClientDetailPageTID(), []);
    const { t: clientDetailTexts } = useLocaleKeys(clientDetailPageTextsPath);
    const { openGlobalBillModal } = useGlobalBillClient();
    const openEditClient = useOpenEditClientModal();
    const client = useMemo(()=>clients.find((c)=>c.id === clientId), [
        clients,
        clientId
    ]);
    const { urlState, changeUrlState } = useStateFromUrl({
        tab: {
            defaultValue: DEFAULT_CLIENT_DETAIL_TAB
        }
    });
    const currentTab = CLIENT_DETAIL_TABS.includes(urlState.tab) ? urlState.tab : DEFAULT_CLIENT_DETAIL_TAB;
    const onBack = ()=>history.push(routes.clients);
    if (client == null) {
        return /*#__PURE__*/ _jsx(Page, {
            testId: TID.Root(),
            header: clientDetailTexts.tabs.summary(),
            onClickBack: onBack,
            children: /*#__PURE__*/ _jsx(Typography, {
                testId: TID.NotFound(),
                children: clientDetailTexts.notFound()
            })
        });
    }
    const statusBadgeSkin = client.status === 'active' ? 'success' : 'default';
    const tabItems = CLIENT_DETAIL_TABS.map((tab)=>({
            id: tab,
            value: tab,
            component: clientDetailTexts.tabs[tab](),
            testId: TID.Tab(tab)
        }));
    return /*#__PURE__*/ _jsxs(Page, {
        testId: TID.Root(),
        header: /*#__PURE__*/ _jsxs(Box, {
            className: styles.headerRow,
            children: [
                /*#__PURE__*/ _jsx(Typography, {
                    weight: "bold",
                    variant: "h3",
                    testId: TID.ClientName(),
                    children: client.name
                }),
                client.status != null && /*#__PURE__*/ _jsx(Badge, {
                    skin: statusBadgeSkin,
                    rounded: true,
                    outlined: true,
                    testId: TID.StatusBadge(),
                    children: clientDetailTexts.status[client.status]()
                })
            ]
        }),
        subheader: client.type != null && /*#__PURE__*/ _jsx(Box, {
            className: styles.subheader,
            children: /*#__PURE__*/ _jsx(Typography, {
                testId: TID.ClientType(),
                skin: "secondary",
                children: clientDetailTexts.header.type[client.type]()
            })
        }),
        actions: /*#__PURE__*/ _jsx(DropdownMenu, {
            gap: 0,
            target: /*#__PURE__*/ _jsx(IconButton, {
                bi: {
                    name: 'client_detail_menu'
                },
                testId: TID.Menu(),
                skin: "transparent",
                children: /*#__PURE__*/ _jsx(Kebab, {
                    size: 20
                })
            }),
            children: /*#__PURE__*/ _jsx(DropdownMenuItem, {
                bi: {
                    name: 'client_detail_edit'
                },
                testId: TID.MenuEdit(),
                iconStart: /*#__PURE__*/ _jsx(Edit, {}),
                onClick: ()=>openEditClient({
                        clientId: client.id,
                        initialName: client.name,
                        initialClientType: client.type
                    }),
                children: clientDetailTexts.header.menu.editClient()
            })
        }),
        mobile: {
            showActions: true
        },
        onClickBack: onBack,
        children: [
            /*#__PURE__*/ _jsx(Tabs, {
                testId: TID.TabBar(),
                bi: {
                    name: 'client_detail_tabs'
                },
                value: currentTab,
                onChange: (_event, value)=>changeUrlState({
                        tab: value
                    }),
                tabs: tabItems
            }),
            /*#__PURE__*/ _jsxs(Box, {
                className: styles.tabContent,
                testId: TID.TabContent(),
                children: [
                    currentTab === 'summary' && /*#__PURE__*/ _jsx(SummaryTab, {
                        clientId: clientId,
                        onManageBilling: ()=>changeUrlState({
                                tab: 'upcomingInvoices'
                            }),
                        onViewUpcomingInvoice: ()=>changeUrlState({
                                tab: 'upcomingInvoices'
                            }),
                        onBillClient: openGlobalBillModal,
                        onViewAllOverdue: ()=>history.push("".concat(routes.client(clientId), "?tab=issuedInvoices&status=overdue"))
                    }),
                    currentTab === 'upcomingInvoices' && /*#__PURE__*/ _jsx(UpcomingInvoicesTab, {
                        clientId: clientId,
                        onBillClient: openGlobalBillModal
                    }),
                    currentTab === 'issuedInvoices' && /*#__PURE__*/ _jsx(IssuedInvoicesTab, {
                        clientId: clientId
                    })
                ]
            })
        ]
    });
}
